import React, { useCallback, useState } from 'react';
import { UploadFile } from '../../../context/models/upload/UploadFile';
import { PreviewImage } from './PreviewImage';
import no_img from '../../../assets/images/picture.png';
import './ImageBox.css';
import { uploadApi } from '../../../context/api/uploadApi';
import { Button as ShadButton } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Upload, X } from 'lucide-react';

type IImageBoxProps = {
	name: string;
	value: string;
	file_name?: string;
	onValueChanged?: (path?: string) => void;
	onDataChanged?: (image?: UploadFile) => void;
	label?: string;
	error?: string;
	sizeClass?: 'xs' | 'sm' | 'md' | 'lg';
	helpBlock?: string;
	labelSmall?: boolean;
	isDisabled?: boolean;
	hasValid?: boolean;
	clearable?: boolean;
};

export const ImageBox = (props: IImageBoxProps) => {
	const {
		isDisabled: disabled,
		value,
		error,
		name,
		label,
		helpBlock,
		file_name,
		labelSmall,
		hasValid,
		clearable = true,
		onValueChanged,
		onDataChanged,
	} = props;
	const showError = !!error;

	const [showPreview, setShowPreview] = useState<boolean>(false);

	const handleSelectImage = useCallback(() => {
		const input = document.createElement('input');

		input.setAttribute('type', 'file');
		input.setAttribute('accept', 'image/*');
		input.click();

		input.onchange = async () => {
			const files = input.files;
			if (files && files.length > 0) {
				const file = files[0];
				try {
					const formData = new FormData();
					formData.append('files', file);
					const response = await uploadApi.UploadImagesAsync({
						files: [file],
						saveName: 'images',
					});
					if (response.isSuccess) {
						const uploadedFiles = response.result as UploadFile[];
						if (uploadedFiles && uploadedFiles.length > 0) {
							const uploaded = uploadedFiles[0];
							setShowPreview(true);
							onValueChanged?.(uploaded.path);
							onDataChanged?.(uploaded);
						}
					}
				} catch (error) {
					console.error('Error uploading image:', error);
				}
			}
		};
	}, [onValueChanged, onDataChanged]);

	const handleRemoveImage = useCallback(() => {
		onValueChanged?.('');
		onDataChanged?.(undefined);
	}, [onValueChanged, onDataChanged]);

	const sizeMap = {
		xs: { container: 'w-24 h-24', button: 'sm' },
		sm: { container: 'w-32 h-32', button: 'sm' },
		md: { container: 'w-40 h-40', button: 'default' },
		lg: { container: 'w-48 h-48', button: 'lg' },
	};

	const currentSize = sizeMap[props.sizeClass as keyof typeof sizeMap] || sizeMap.md;

	return (
		<div className={cn("space-y-2")}>
			{label && (
				<Label htmlFor={name} className={cn(labelSmall && "text-xs")}>
					{label} {hasValid && <span className='text-destructive'>(*)</span>}
				</Label>
			)}
			<div className={cn("space-y-3", showError && "rounded-lg border border-destructive p-3")}>
				<div className={cn(
					"flex items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 cursor-pointer transition-colors hover:bg-muted",
					"flex-col gap-4 p-4",
					currentSize.container,
					disabled && "cursor-not-allowed opacity-50"
				)}
					onClick={() => !disabled && handleSelectImage()}
				>
					{value ? (
						<img
							src={value}
							alt={file_name || 'uploaded'}
							className="w-full h-full object-contain rounded"
							onLoad={(e) => {
								const img = e.currentTarget;
								if (img.naturalWidth > img.naturalHeight) {
									img.style.width = '100%';
								} else {
									img.style.height = '100%';
								}
							}}
						/>
					) : (
						<div className="flex flex-col items-center gap-2 text-center">
							<Upload className="h-8 w-8 text-muted-foreground" />
							<img src={no_img} alt='no img' className='h-12 w-12 opacity-75' />
						</div>
					)}
				</div>

				<div className='flex gap-2 justify-center'>
					<ShadButton
						type="button"
						variant="default"
						size={currentSize.button as any}
						onClick={handleSelectImage}
						disabled={disabled}
						className="gap-2"
					>
						<Upload className="h-4 w-4" />
						Chọn ảnh
					</ShadButton>
					{value && clearable && (
						<ShadButton
							type="button"
							variant="destructive"
							size={currentSize.button as any}
							onClick={handleRemoveImage}
							disabled={disabled}
							className="gap-2"
						>
							<X className="h-4 w-4" />
							Xóa ảnh
						</ShadButton>
					)}
				</div>
			</div>
			{showError && <p className="text-sm font-medium text-destructive">{error}</p>}
			{helpBlock && <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: helpBlock }} />}
			{showPreview && (
				<PreviewImage
					image={value}
					onClose={() => setShowPreview(false)}
				/>
			)}
		</div>
	);
};
